import { Preferences } from '@capacitor/preferences';

const undefinedSetting = '**UNDEFINED**';
const nullSetting = '**NULL**';

function normalizeLocale(localeTag: string): string {
  const supportedLocales = ['da-DK', 'en-GB', 'de-DE', 'sv-SE', 'lv-LV', 'pl-PL'];

  if (localeTag.includes('-') && supportedLocales.includes(localeTag)) {
    return localeTag;
  }

  const localeMap: Record<string, string> = {
    'da': 'da-DK',
    'en': 'en-GB',
    'de': 'de-DE',
    'sv': 'sv-SE',
    'lv': 'lv-LV',
    'pl': 'pl-PL'
  };

  return localeMap[localeTag.toLowerCase()] ?? 'en-GB';
}

const deviceLocale = navigator.language;
const normalizedLocale = normalizeLocale(deviceLocale);
interface DeviceSettingsState {
  homepageLayout: Record<HomepageElement, boolean>;
  homepageLayoutOrder: HomepageElement[];
  language: string;
  materialGroupAddedItems: boolean;
  saveImageToDevice: boolean;
  theme: ThemeSetting;
  uploadOnCellular: boolean;
  calendarView: CalendarViewType;
  defaultMap: MapSetting;
  userDefinedPeriodHourRegistration: number;
  convertHeic: boolean;
  firstLoginFlag: boolean;
}

interface UserSettingsState {
  calendarFilter: CalendarFilter | null;
  calendarFilterSave: boolean;
  cartItem: MaterialItemApp[];
  defaultFitterCategoryID: number | null;
  defaultMaterialLocation: LocationDTO | null;
  hourRegistrationOverviewState: string[] | undefined;
  materialFilterCatalog: MaterialFilterCatalog | null;
  materialsStartTab: MaterialStartTabSetting;
  openProjectOnCreation: boolean;
  projectsAssignProjectManager: boolean;
  projectsAssignTeamLead: boolean;
  useDefaultFitterCategory: boolean;
  showWorksheetsToday: boolean;
  worksheetsShowAllTab: boolean;
  worksheetsShowDescription: boolean;
  worksheetsShowResponsibleTab: boolean;
  worksheetsStartTab: StartTabSetting;
  worksheetStopwatch: StopwatchData | null;
  expandedWorkSheetAccordions: string[] | undefined;
  lastQALocationId: Record<number, number | undefined>;
  hideEmptyWeekendsInOverview: boolean;
  showNonStandardUnitHourRegistrations: boolean;
  showNonStandardUnitHourRegistrationsDashboard: boolean;
  showWholesalerCatalogItemImages: boolean;
  showOwnItemImages: boolean;
  drawColor: string | undefined,
  drawWidth: number | undefined,
  showScannerButton: boolean;
  favoriteListIds: number[];
}

const deviceDefaults: DeviceSettingsState = {
  homepageLayout: {
    [HomepageElement.HourRegistrationOverview]: true,
    [HomepageElement.WorksheetActiveTimer]: false,
    [HomepageElement.NextCalendarEvent]: false,
    [HomepageElement.NextWorksheet]: false,
    [HomepageElement.OverdueWorksheet]: false
  },
  calendarView: CalendarViewType.Month,
  homepageLayoutOrder: Object.values(HomepageElement),
  language: normalizedLocale,
  materialGroupAddedItems: false,
  saveImageToDevice: true,
  theme: ThemeSetting.light,
  uploadOnCellular: true,
  defaultMap: MapSetting.apple,
  userDefinedPeriodHourRegistration: 1,
  convertHeic: true,
  firstLoginFlag: true
};

const userDefaults: UserSettingsState = {
  calendarFilter: null,
  calendarFilterSave: false,
  cartItem: [],
  defaultFitterCategoryID: null,
  defaultMaterialLocation: null,
  hourRegistrationOverviewState: undefined,
  materialFilterCatalog: null,
  materialsStartTab: MaterialStartTabSetting.wholesalerCatalog,
  openProjectOnCreation: false,
  projectsAssignProjectManager: true,
  projectsAssignTeamLead: false,
  useDefaultFitterCategory: true,
  showWorksheetsToday: true,
  worksheetsShowAllTab: false,
  worksheetsShowDescription: false,
  worksheetsShowResponsibleTab: false,
  worksheetsStartTab: StartTabSetting.mine,
  worksheetStopwatch: null,
  expandedWorkSheetAccordions: undefined,
  lastQALocationId: {},
  hideEmptyWeekendsInOverview: true,
  showNonStandardUnitHourRegistrations: false,
  showNonStandardUnitHourRegistrationsDashboard: false,
  showWholesalerCatalogItemImages: true,
  showOwnItemImages: false,
  drawColor: undefined,
  drawWidth: undefined,
  showScannerButton: false,
  favoriteListIds: []
};

type AllSettingsState = DeviceSettingsState & UserSettingsState;

const allDefaults = { ...deviceDefaults, ...userDefaults };

// This store uses a couple of type assertions to work around losing type information when storing values in JSON.
export const useSettingsStore = defineStore('Settings', () => {
  const userConfigStore = useUserConfigurationStore();

  const settings = ref<AllSettingsState>(JsonCopy(allDefaults));

  function toFullKey(key: keyof AllSettingsState) {

    if (key in userDefaults) {
      return `${userConfigStore.config.SiteName}-${userConfigStore.fitterId}_${key}`;
    } else {
      return key;
    }
  }

  async function loadDeviceSettings() {
    const promises = [];

    for (const key in deviceDefaults) {
      promises.push(load(key as keyof DeviceSettingsState));
    }

    await Promise.all(promises);
  }

  async function loadUserSettings() {
    const promises = [];

    for (const key in userDefaults) {
      promises.push(load(key as keyof DeviceSettingsState));
    }

    await Promise.all(promises);
  }

  async function load<T extends keyof AllSettingsState>(key: T) {
    const { value: json } = await Preferences.get({ key: toFullKey(key) });

    if (json !== null) {
      let value = JSON.parse(json);

      // Custom handling for null & undefined values since they can't be represented in JSON
      switch (value) {
        case undefinedSetting:
          value = undefined;
          break;

        case nullSetting:
          value = null;
          break;
      }

      settings.value[key] = value as AllSettingsState[T];
    }
  }

  async function set<T extends keyof AllSettingsState>(key: T, value: AllSettingsState[T]) {
    let sanitizedValue;

    // Custom handling for null & undefined values since they can't be represented in JSON
    switch (value) {
      case undefined:
        sanitizedValue = undefinedSetting;
        break;

      case null:
        sanitizedValue = nullSetting;
        break;

      default:
        sanitizedValue = value;
        break;
    }

    await Preferences.set({ key: toFullKey(key), value: JSON.stringify(sanitizedValue) });
    settings.value[key] = value;
  }

  /**
   * Useful in scenarios where the readonly types dont work but you dont want to risk changing the original settings object
   * @param key name of the setting to copy
   * @returns a deep copy of the setting
   */
  function deepCopySetting<T extends keyof AllSettingsState>(key: T) {
    return JsonCopy(settings.value[key]);
  }

  async function clearDeviceSettings() {
    const promises: Promise<unknown>[] = [];

    for (const key in deviceDefaults) {
      promises.push(Preferences.remove({ key: toFullKey(key as keyof DeviceSettingsState) }));
    }

    Promise.all(promises);

    settings.value = { ...settings.value, ...JsonCopy(deviceDefaults) };
  }

  async function clearCurrentUserSettings() {
    const promises: Promise<unknown>[] = [];

    for (const key in userDefaults) {
      promises.push(Preferences.remove({ key: toFullKey(key as keyof UserSettingsState) }));
    }

    Promise.all(promises);

    settings.value = { ...settings.value, ...JsonCopy(userDefaults) };
  }

  async function clearDeviceAndCurrentUserSettings() {
    const promises: Promise<unknown>[] = [];

    for (const key in allDefaults) {
      promises.push(Preferences.remove({ key: toFullKey(key as keyof AllSettingsState) }));
    }

    Promise.all(promises);

    settings.value = { ...JsonCopy(allDefaults) };
  }

  async function clearAllSettings() {
    await Preferences.clear();
    settings.value = JsonCopy(allDefaults);
  }

  function resetUserSettings() {
    settings.value = { ...settings.value, ...JsonCopy(userDefaults) };
  }

  async function $reset() {
    clearAllSettings();
  }

  return {
    _settings: settings,
    settings: readonly(settings),
    loadDeviceSettings,
    loadUserSettings,
    deepCopySetting,
    set,
    clearDeviceSettings,
    clearCurrentUserSettings,
    clearDeviceAndCurrentUserSettings,
    clearAllSettings,
    resetUserSettings,
    $reset
  };
});
